type AnalyticsEventValue = string | number | boolean | string[];
type AnalyticsEventParams = Record<string, AnalyticsEventValue>;
type GtagFunction = (
  command: "event" | "config" | "js",
  eventNameOrDate: string | Date,
  eventParams?: AnalyticsEventParams
) => void;
type FacebookPixelCallArguments =
  | [command: "init", pixelId: string]
  | [command: "track" | "trackCustom", eventName: string, eventParams?: AnalyticsEventParams];

type FacebookPixelStandardEventName =
  | "PageView"
  | "ViewContent"
  | "Search"
  | "Contact"
  | "Lead"
  | "CompleteRegistration";

interface FacebookPixelFunction {
  (...args: FacebookPixelCallArguments): void;
  callMethod?: (...args: FacebookPixelCallArguments) => void;
  queue?: FacebookPixelCallArguments[];
  loaded?: boolean;
  version?: string;
  push?: (...args: FacebookPixelCallArguments) => number;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
    fbq?: FacebookPixelFunction;
    _fbq?: FacebookPixelFunction;
  }
}

const configuredGoogleAnalyticsIds = new Set<string>();
const configuredFacebookPixelIds = new Set<string>();

function normalizeHostname(hostname?: string | null) {
  return (hostname || "")
    .trim()
    .replace(/^\[|\]$/g, "")
    .toLowerCase();
}

function normalizeValue(value?: string | null) {
  const normalizedValue = value?.trim();
  return normalizedValue?.length ? normalizedValue : null;
}

function isAdminProductionEnvironment() {
  return normalizeValue(process.env.REACT_APP_ENVIRONMENT)?.toLowerCase() === "production";
}

function resolveAdminAnalyticsHostnames() {
  const configuredCookieDomain = process.env.REACT_APP_AUTH_COOKIE_DOMAIN?.trim()
    .replace(/^\.+/, "")
    .toLowerCase();

  if (!configuredCookieDomain) {
    return [];
  }

  return [`admin.${configuredCookieDomain}`];
}

function isProductionBrowserRuntimeEnabled(identifier?: string | null) {
  if (!isAdminProductionEnvironment() || typeof window === "undefined") {
    return false;
  }

  const normalizedIdentifier = normalizeValue(identifier);
  if (!normalizedIdentifier) {
    return false;
  }

  return resolveAdminAnalyticsHostnames().includes(normalizeHostname(window.location.hostname));
}

function sanitizeEventParams(
  eventParams?: Record<string, string | number | boolean | null | undefined>
): AnalyticsEventParams {
  if (!eventParams) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(eventParams).filter(([, value]) => value !== null && value !== undefined)
  ) as AnalyticsEventParams;
}

function resolveAdminStandardFacebookPixelEvent(
  eventName: string,
  baseEventParams: AnalyticsEventParams
): {
  standardEventName: FacebookPixelStandardEventName;
  standardEventParams: AnalyticsEventParams;
} | null {
  if (eventName === "mochi_chat_first_message") {
    return {
      standardEventName: "Contact",
      standardEventParams: {
        ...baseEventParams,
        content_type: "mochi_chat_widget",
      },
    };
  }

  if (eventName === "mochi_chat_event_created") {
    return {
      standardEventName: "Lead",
      standardEventParams: {
        ...baseEventParams,
        content_type: "mochi_chat_widget",
      },
    };
  }

  if (eventName === "admin_registration_completed") {
    return {
      standardEventName: "CompleteRegistration",
      standardEventParams: baseEventParams,
    };
  }

  return null;
}

function ensureGoogleAnalyticsInitialized(measurementId: string) {
  if (!isProductionBrowserRuntimeEnabled(measurementId)) {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer?.push(arguments);
    };

  if (!configuredGoogleAnalyticsIds.has(measurementId)) {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { send_page_view: false });
    configuredGoogleAnalyticsIds.add(measurementId);
  }

  const scriptElementId = `ga-script-${measurementId}`;
  if (!document.getElementById(scriptElementId)) {
    const script = document.createElement("script");
    script.id = scriptElementId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  return true;
}

function ensureFacebookPixelInitialized(pixelId: string) {
  if (!isProductionBrowserRuntimeEnabled(pixelId)) {
    return false;
  }

  if (typeof window.fbq !== "function") {
    let fbq: FacebookPixelFunction;
    fbq = (...args: FacebookPixelCallArguments) => {
      if (typeof fbq.callMethod === "function") {
        fbq.callMethod(...args);
        return;
      }

      fbq.queue = fbq.queue || [];
      fbq.queue.push(args);
    };

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.push = (...args) => {
      fbq.queue = fbq.queue || [];
      fbq.queue.push(args);
      return fbq.queue.length;
    };

    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!configuredFacebookPixelIds.has(pixelId)) {
    window.fbq("init", pixelId);
    configuredFacebookPixelIds.add(pixelId);
  }

  const scriptElementId = `facebook-pixel-script-${pixelId}`;
  if (!document.getElementById(scriptElementId)) {
    const script = document.createElement("script");
    script.id = scriptElementId;
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  return true;
}

function getGoogleAnalyticsId() {
  return process.env.REACT_APP_GOOGLE_ANALYTICS_ID?.trim() || "";
}

function getFacebookPixelId() {
  return process.env.REACT_APP_FACEBOOK_PIXEL_ID?.trim() || "";
}

export function initializeAdminMarketingAnalytics() {
  ensureGoogleAnalyticsInitialized(getGoogleAnalyticsId());
  ensureFacebookPixelInitialized(getFacebookPixelId());
}

export function trackAdminPageView(pathname: string, search: string) {
  const googleAnalyticsId = getGoogleAnalyticsId();
  if (ensureGoogleAnalyticsInitialized(googleAnalyticsId) && typeof window.gtag === "function") {
    const pagePath = search ? `${pathname}${search}` : pathname;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: pagePath,
      page_title: document.title,
      send_to: googleAnalyticsId,
    });
  }

  const facebookPixelId = getFacebookPixelId();
  if (ensureFacebookPixelInitialized(facebookPixelId) && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
}

export function trackAdminAnalyticsEvent(
  eventName: string,
  eventParams?: Record<string, string | number | boolean | null | undefined>
) {
  const normalizedEventName = normalizeValue(eventName);
  if (!normalizedEventName) {
    return;
  }

  const normalizedEventParams = sanitizeEventParams(eventParams);
  const googleAnalyticsId = getGoogleAnalyticsId();
  if (ensureGoogleAnalyticsInitialized(googleAnalyticsId) && typeof window.gtag === "function") {
    window.gtag("event", normalizedEventName, normalizedEventParams);
  }

  const facebookPixelId = getFacebookPixelId();
  if (ensureFacebookPixelInitialized(facebookPixelId) && typeof window.fbq === "function") {
    const standardFacebookPixelEvent = resolveAdminStandardFacebookPixelEvent(
      normalizedEventName,
      normalizedEventParams
    );
    if (standardFacebookPixelEvent) {
      window.fbq(
        "track",
        standardFacebookPixelEvent.standardEventName,
        standardFacebookPixelEvent.standardEventParams
      );
    }

    window.fbq("trackCustom", normalizedEventName, normalizedEventParams);
  }
}
